import axios from "axios";
import {
    LOGIN_SUCCESS,
    LOGIN_FAIL,
    LOGOUT,
    FETCH_USER_SUCCESS,
    FETCH_USER_FAIL,
    REGISTER_SUCCESS,
    SET_MESSAGE,
    REGISTER_FAIL,
} from "../constants/types";
import { clearAccessToken, setAccessTokenFromResponse } from "./authTokenStore";
import {
    refreshAccessToken as refreshAccessTokenShared,
    refreshAccessTokenWithStatus as refreshAccessTokenWithStatusShared,
    isAuthRefreshSuspended,
    resumeAuthRefresh,
    shouldSkipAuthRefresh,
} from "./authRefreshCoordinator";
import { clearUserScopedClientState } from "./clientSessionLifecycle";
import {
    clearPendingLoginChallenge,
} from "./authLoginChallengeStorage";
import {
    clearPendingRegistrationChallenge,
} from "./authRegistrationChallengeStorage";
import { persistPostLoginRedirect } from "./authPostLoginRedirectStorage";

let refreshInterceptorAttached = false;
const PUBLIC_AUTH_REQUEST_CONFIG = {
    withCredentials: true,
    skipAuthRefresh: true,
};

const normalizeAuthBaseUrl = (value) => {
    if (typeof value !== "string") {
        return "";
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return "";
    }
    const withoutTrailingSlashes = trimmed.replace(/\/+$/, "");
    return withoutTrailingSlashes.replace(/\/api$/i, "");
};

const AUTH_BASE_URL =
    normalizeAuthBaseUrl(process.env.REACT_APP_API_URL)
    || normalizeAuthBaseUrl(process.env.REACT_APP_USER_SERVICE_URL)
    || "https://muzone.co";

const AUTH_ENDPOINTS = {
    LOGIN: `${AUTH_BASE_URL}/api/auth/login`,
    LOGIN_CHALLENGE_COMPLETE: `${AUTH_BASE_URL}/api/auth/login/challenge/complete`,
    LOGIN_CHALLENGE_RESEND: `${AUTH_BASE_URL}/api/auth/login/challenge/resend`,
    REGISTER: `${AUTH_BASE_URL}/api/auth/register`,
    REGISTER_EMAIL_START: `${AUTH_BASE_URL}/api/auth/register/email/start`,
    REGISTER_EMAIL_VERIFY: `${AUTH_BASE_URL}/api/auth/register/email/verify`,
    REGISTER_EMAIL_RESEND: `${AUTH_BASE_URL}/api/auth/register/email/resend`,
    PASSWORD_RESET_START: `${AUTH_BASE_URL}/api/auth/password-reset/start`,
    PASSWORD_RESET_COMPLETE: `${AUTH_BASE_URL}/api/auth/password-reset/complete`,
    LOGOUT: `${AUTH_BASE_URL}/api/auth/logout`,
    CURRENT_USER: `${AUTH_BASE_URL}/api/auth/me`,
    SSO_START: (provider) => `${AUTH_BASE_URL}/api/auth/sso/${provider}/start`,
    SSO_CALLBACK: (provider) => `${AUTH_BASE_URL}/api/auth/sso/${provider}/callback`,
};

export const ACCOUNT_DELETION_RECOVERY_REQUIRED_CODE = "ACCOUNT_DELETION_RECOVERY_REQUIRED";

export const isAccountDeletionRecoveryRequired = (error) => (
    error?.response?.data?.code === ACCOUNT_DELETION_RECOVERY_REQUIRED_CODE
);

export const refreshAccessTokenWithStatus = async () => {
    return refreshAccessTokenWithStatusShared({ trigger: "auth-service" });
};

export const refreshAccessToken = async () => {
    return refreshAccessTokenShared({ trigger: "auth-service" });
};

const attachAuthRefreshInterceptor = () => {
    if (refreshInterceptorAttached) {
        return;
    }
    refreshInterceptorAttached = true;

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const status = error?.response?.status;
            const originalRequest = error?.config;

            if (status === 401 && originalRequest && !originalRequest._retry && !shouldSkipAuthRefresh(originalRequest)) {
                originalRequest._retry = true;
                const refreshResult = await refreshAccessTokenWithStatusShared({
                    trigger: `axios-401:${originalRequest.url || "unknown"}`,
                });
                if (refreshResult.ok) {
                    return axios(originalRequest);
                }
            }
            return Promise.reject(error);
        }
    );
};

attachAuthRefreshInterceptor();

const isLoginChallengeResponse = (response) => (
    response?.status === 202
    && typeof response?.data?.challengeId === "string"
    && response.data.challengeId.trim() !== ""
);

const clearAuthenticatedState = (dispatch) => {
    clearAccessToken();
    dispatch({ type: LOGOUT });
};

const applyAuthenticatedLogin = (dispatch, data, message = null) => {
    clearPendingLoginChallenge();
    clearPendingRegistrationChallenge();
    resumeAuthRefresh();
    setAccessTokenFromResponse(data);
    dispatch({
        type: LOGIN_SUCCESS,
        payload: data,
    });
    if (typeof message === "string" && message.trim() !== "") {
        dispatch({
            type: SET_MESSAGE,
            payload: message,
        });
    }
    return data;
};

export const register = (username, email, password) => (dispatch) => {
    return axios.post(AUTH_ENDPOINTS.REGISTER, {
        username,
        email,
        password
    }, { withCredentials: true })
        .then((response) => {
            clearPendingRegistrationChallenge();
            resumeAuthRefresh();
            setAccessTokenFromResponse(response?.data);
            dispatch({ type: REGISTER_SUCCESS });
            dispatch({ type: SET_MESSAGE, payload: response.data.message });
            return Promise.resolve();
        })
        .catch((error) => {
            const message = error.response?.data?.message || "Registration failed";
            dispatch({ type: REGISTER_FAIL });
            dispatch({ type: SET_MESSAGE, payload: message });
            return Promise.reject();
        });
};

export const startEmailRegistration = (username, email, password) => async () => {
    const response = await axios.post(
        AUTH_ENDPOINTS.REGISTER_EMAIL_START,
        {
            username,
            email,
            password,
        },
        PUBLIC_AUTH_REQUEST_CONFIG
    );
    return response.data;
};

export const resendEmailRegistrationCode = (challengeId) => async () => {
    const response = await axios.post(
        AUTH_ENDPOINTS.REGISTER_EMAIL_RESEND,
        { challengeId },
        PUBLIC_AUTH_REQUEST_CONFIG
    );
    return response.data;
};

export const verifyEmailRegistration = (challengeId, code, password) => async (dispatch) => {
    const payload = {
        challengeId,
        code,
    };
    if (typeof password === "string" && password.length > 0) {
        payload.password = password;
    }

    const response = await axios.post(
        AUTH_ENDPOINTS.REGISTER_EMAIL_VERIFY,
        payload,
        PUBLIC_AUTH_REQUEST_CONFIG
    );

    return applyAuthenticatedLogin(dispatch, response.data);
};

export const startPasswordReset = (email) => async () => {
    const response = await axios.post(
        AUTH_ENDPOINTS.PASSWORD_RESET_START,
        { email },
        PUBLIC_AUTH_REQUEST_CONFIG
    );
    return response.data;
};

export const completePasswordReset = (token, newPassword) => async (dispatch) => {
    const response = await axios.post(
        AUTH_ENDPOINTS.PASSWORD_RESET_COMPLETE,
        {
            token,
            newPassword,
        },
        PUBLIC_AUTH_REQUEST_CONFIG
    );

    await clearUserScopedClientState({ dispatch });
    return response.data;
};

// Login Action
export const login = (email, password) => async (dispatch) => {
    try {
        const response = await axios.post(
            AUTH_ENDPOINTS.LOGIN,
            { email, password },
            { withCredentials: true }
        );
        if (isLoginChallengeResponse(response)) {
            clearAuthenticatedState(dispatch);
            return Promise.resolve(response.data);
        }

        applyAuthenticatedLogin(dispatch, response.data, "Login successful");

        // Return the actual data so your component can do `.then((data) => ...)`
        return Promise.resolve(response.data);
    } catch (error) {
        if (isAccountDeletionRecoveryRequired(error)) {
            clearAuthenticatedState(dispatch);
            dispatch({ type: LOGIN_FAIL });
            dispatch({
                type: SET_MESSAGE,
                payload: error.response.data.message || "Account recovery is required.",
            });
            return Promise.reject(error);
        }

        // Grab an error message from the server (structure may vary)
        let message = "Login failed";
        if (error.response && error.response.data && error.response.data.message) {
            message = error.response.data.message;
        }

        // Dispatch fail
        dispatch({ type: LOGIN_FAIL });
        // Dispatch the error message so Redux stores it
        dispatch({
            type: SET_MESSAGE,
            payload: message,
        });

        // Provide the error so the calling code knows login failed
        return Promise.reject(error);
    }
};

export const completeLoginChallenge = (challengeId, code) => async (dispatch) => {
    try {
        const response = await axios.post(
            AUTH_ENDPOINTS.LOGIN_CHALLENGE_COMPLETE,
            {
                challengeId,
                code,
            },
            PUBLIC_AUTH_REQUEST_CONFIG
        );

        applyAuthenticatedLogin(dispatch, response.data, "Login successful");
        return Promise.resolve(response.data);
    } catch (error) {
        clearAuthenticatedState(dispatch);
        return Promise.reject(error);
    }
};

export const resendLoginChallenge = (challengeId) => async () => {
    const response = await axios.post(
        AUTH_ENDPOINTS.LOGIN_CHALLENGE_RESEND,
        { challengeId },
        PUBLIC_AUTH_REQUEST_CONFIG
    );
    return response.data;
};

// Fetch Current User Action (For persistent login)
export const fetchCurrentUser = () => async (dispatch) => {
    if (isAuthRefreshSuspended()) {
        dispatch({ type: FETCH_USER_FAIL });
        return;
    }

    try {
        const response = await axios.get(AUTH_ENDPOINTS.CURRENT_USER, { withCredentials: true });

        if (response.data.id) {
            clearPendingLoginChallenge();
            clearPendingRegistrationChallenge();
            resumeAuthRefresh();
            dispatch({
                type: FETCH_USER_SUCCESS,
                payload: response.data,
            });
            await refreshAccessTokenWithStatusShared({
                trigger: "auth-current-user-hydration",
            });
        } else {
            dispatch({ type: FETCH_USER_FAIL });
        }
    } catch (error) {
        dispatch({ type: FETCH_USER_FAIL });
    }
};

// Logout Action
export const logout = () => async (dispatch) => {
    let serverLogoutSucceeded = false;
    try {
        await axios.post(AUTH_ENDPOINTS.LOGOUT, null, { withCredentials: true });
        serverLogoutSucceeded = true;
    } catch (error) {
        // Local teardown is mandatory even when the network logout fails.
    }
    await clearUserScopedClientState({ dispatch });
    return { serverLogoutSucceeded };
};

export const startSso = async (provider, postLoginRedirect = "/") => {
    const response = await axios.post(
        AUTH_ENDPOINTS.SSO_START(provider),
        null,
        { withCredentials: true }
    );
    const authorizeUrl = response.data?.authorizeUrl;
    if (authorizeUrl) {
        persistPostLoginRedirect(postLoginRedirect);
    }
    return authorizeUrl;
};

export const completeSsoLogin = (provider, code, state) => async (dispatch) => {
    try {
        const response = await axios.post(
            AUTH_ENDPOINTS.SSO_CALLBACK(provider),
            { code, state },
            { withCredentials: true }
        );
        if (isLoginChallengeResponse(response)) {
            clearPendingLoginChallenge();
            clearAuthenticatedState(dispatch);
            throw new Error("Unexpected login challenge response for SSO callback");
        }

        applyAuthenticatedLogin(dispatch, response.data, "Login successful");
        return Promise.resolve(response.data);
    } catch (error) {
        dispatch({ type: LOGIN_FAIL });
        return Promise.reject(error);
    }
};

// Define and Export `authReducer`
const initialState = { isLoggedIn: false, user: null };

const authReducer = (state = initialState, action) => {
    const { type, payload } = action;

    switch (type) {
        case REGISTER_SUCCESS:
            return {
                ...state,
                isLoggedIn: true,
            };
        case REGISTER_FAIL:
            return {
                ...state,
                isLoggedIn: false,
            };
        case LOGIN_SUCCESS:
            return {
                ...state,
                isLoggedIn: true,
                user: payload,
            };
        case LOGIN_FAIL:
            return {
                ...state,
                isLoggedIn: false,
                user: null,
            };
        case LOGOUT:
            return {
                ...state,
                isLoggedIn: false,
                user: null,
            };
        case FETCH_USER_SUCCESS:
            return {
                ...state,
                isLoggedIn: true,
                user: payload,
            };
        case FETCH_USER_FAIL:
            return {
                ...state,
                isLoggedIn: false,
                user: null,
            };
        default:
            return state;
    }
};

export default authReducer;
